'use client';

import { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Stack,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import RemoveDialog from '../../common/components/RemoveDialog';
import { useTranslation } from '../../common/components/LocalizationProvider';

/**
 * -------------------------------------------------------
 * CollectionActions.jsx
 * - Mantém a lógica completa original (RemoveDialog, customActions, mobile)
 * - Harmonizado com o novo tema (dark/light)
 * - Ícones e cores padronizados com DataTable moderno
 * -------------------------------------------------------
 */

const useStyles = makeStyles()(() => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
}));

const CollectionActions = ({
  itemId,
  editPath,
  endpoint,
  setTimestamp,
  customActions,
  readonly,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const t = useTranslation();

  const phone = useMediaQuery(theme.breakpoints.down('sm'));

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [removing, setRemoving] = useState(false);

  // -------------------------------------------------------
  // Handlers
  // -------------------------------------------------------
  const handleEdit = () => {
    navigate(`${editPath}/${itemId}`);
    setMenuAnchorEl(null);
  };

  const handleRemove = () => {
    setRemoving(true);
    setMenuAnchorEl(null);
  };

  const handleCustom = (action) => {
    action.handler(itemId);
    setMenuAnchorEl(null);
  };

  const handleRemoveResult = (removed) => {
    setRemoving(false);
    if (removed) {
      setTimestamp(Date.now());
    }
  };

  // -------------------------------------------------------
  // Mobile menu (MoreVert)
  // -------------------------------------------------------
  const phoneMenuItems = [
    ...(customActions
      ? customActions.map((action) => (
          <MenuItem onClick={() => handleCustom(action)} key={action.key}>
            {action.title}
          </MenuItem>
        ))
      : []),
    ...(!readonly
      ? [
          editPath && (
            <MenuItem onClick={handleEdit} key="edit">
              {t('sharedEdit')}
            </MenuItem>
          ),
          <MenuItem onClick={handleRemove} key="remove">
            {t('sharedRemove')}
          </MenuItem>,
        ]
      : []),
  ].filter(Boolean);

  // -------------------------------------------------------
  // Desktop actions (harmonizadas com DataTable)
  // -------------------------------------------------------
  const desktopActions = [
    ...(customActions
      ? customActions.map((action) => (
          <Tooltip title={action.title} key={action.key} arrow>
            <IconButton
              size="small"
              onClick={() => handleCustom(action)}
              sx={{
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.info.light
                    : theme.palette.primary.main,
                '&:hover': {
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.info.main
                      : theme.palette.primary.dark,
                },
              }}
            >
              {action.icon}
            </IconButton>
          </Tooltip>
        ))
      : []),

    ...(!readonly
      ? [
          editPath && (
            <Tooltip title={t('sharedEdit')} key="edit" arrow>
              <IconButton
                size="small"
                onClick={handleEdit}
                sx={{
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.info.light
                      : theme.palette.primary.main,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.info.main
                        : theme.palette.primary.dark,
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ),

          <Tooltip title={t('sharedRemove')} key="remove" arrow>
            <IconButton
              size="small"
              onClick={handleRemove}
              sx={{
                color: theme.palette.error.main,
                transition: 'color 0.2s ease',
                '&:hover': { color: theme.palette.error.dark },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>,
        ]
      : []),
  ].filter(Boolean);

  // -------------------------------------------------------
  // Renderização final
  // -------------------------------------------------------
  return (
    <>
      {phone ? (
        <div>
          <Tooltip title={t('sharedActions')} arrow>
            <IconButton
              size="small"
              onClick={(event) => setMenuAnchorEl(event.currentTarget)}
              sx={{
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.text.secondary
                    : theme.palette.text.primary,
                '&:hover': {
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.light
                      : theme.palette.primary.main,
                },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            open={!!menuAnchorEl}
            anchorEl={menuAnchorEl}
            onClose={() => setMenuAnchorEl(null)}
          >
            {phoneMenuItems}
          </Menu>
        </div>
      ) : (
        <Stack direction="row" className={classes.row}>
          {desktopActions}
        </Stack>
      )}

      {/* Diálogo de exclusão */}
      <RemoveDialog
        style={{ transform: 'none' }}
        open={removing}
        endpoint={endpoint}
        itemId={itemId}
        onResult={handleRemoveResult}
      />
    </>
  );
};

export default CollectionActions;
